/**
 * schedule.ts - Schedule Module
 *
 * This module handles tournament scheduling functionality including:
 * - Showing round info modals
 * - Managing schedule modal
 * - Loading and displaying round schedules
 * - Saving schedule changes
 */

import type { Round, Tournament } from '../../types/api';
import { TOURNAMENT_FORMATS, API_BASE_URL } from '../utils/constants';
import { t } from '../utils/i18n-helper';

/**
 * Schedule update data
 */
interface ScheduleUpdate {
  roundNumber: number;
  roundDate: string | null;
  startTime: string | null;
}

/**
 * Show round info modal for generating pairings
 * @param tournamentId - The tournament ID
 */
export function showRoundInfoModal(tournamentId: string): void {
  const modal = document.getElementById('round-info-modal');
  const tournamentIdInput = document.getElementById('round-info-tournament-id') as HTMLInputElement | null;
  const roundDateInput = document.getElementById('round-date') as HTMLInputElement | null;
  const startTimeInput = document.getElementById('round-start-time') as HTMLInputElement | null;
  const errorDiv = document.getElementById('round-info-error');

  if (!modal || !tournamentIdInput || !roundDateInput || !startTimeInput || !errorDiv) return;

  tournamentIdInput.value = tournamentId;

  // Set default date to today
  const today = new Date().toISOString().split('T')[0]!;
  roundDateInput.value = today;

  // Clear time
  startTimeInput.value = '';

  errorDiv.textContent = '';
  modal.style.display = 'block';
}

/**
 * Close round info modal
 */
export function closeRoundInfoModal(): void {
  const modal = document.getElementById('round-info-modal');
  const form = document.getElementById('round-info-form') as HTMLFormElement | null;

  if (modal) {
    modal.style.display = 'none';
  }
  if (form) {
    form.reset();
  }
}

/**
 * Handle round info form submission
 * @param event - The form submit event
 */
export async function handleRoundInfoSubmit(event: Event): Promise<void> {
  event.preventDefault();

  const tournamentIdInput = document.getElementById('round-info-tournament-id') as HTMLInputElement | null;
  const roundDateInput = document.getElementById('round-date') as HTMLInputElement | null;
  const startTimeInput = document.getElementById('round-start-time') as HTMLInputElement | null;

  if (!tournamentIdInput || !roundDateInput || !startTimeInput) return;

  const tournamentId = tournamentIdInput.value;
  const roundDate = roundDateInput.value;
  const startTime = startTimeInput.value;

  closeRoundInfoModal();

  // Now actually generate the pairings with round info
  if (window.generatePairingsWithRoundInfo) {
    await window.generatePairingsWithRoundInfo(tournamentId, roundDate, startTime);
  }
}

/**
 * Open schedule modal
 * @param tournamentId - The tournament ID
 * @param format - The tournament format
 * @param numberOfRounds - Number of rounds
 */
export async function openScheduleModal(tournamentId: string, format: string, numberOfRounds: number): Promise<void> {
  const modal = document.getElementById('schedule-modal');
  if (!modal) return;

  modal.style.display = 'block';

  await loadSchedule(tournamentId, format, numberOfRounds);
}

/**
 * Close schedule modal
 */
export function closeScheduleModal(): void {
  const modal = document.getElementById('schedule-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Load and display round schedule
 * @param tournamentId - The tournament ID
 * @param format - The tournament format
 * @param numberOfRounds - Number of rounds
 */
export async function loadSchedule(tournamentId: string, format: string, numberOfRounds: number): Promise<void> {
  const scheduleContent = document.getElementById('schedule-modal-content');
  if (!scheduleContent) return;

  try {
    // Fetch existing rounds data
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/rounds`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to load rounds');
    }

    const existingRounds: Round[] = await response.json();
    const roundsMap: Record<number, Round> = {};
    existingRounds.forEach(round => {
      roundsMap[round.roundNumber] = round;
    });

    // Determine how many rounds to display based on format
    let totalRounds = 0;
    if (format === TOURNAMENT_FORMATS.SWISS) {
      totalRounds = numberOfRounds;
    } else if (format === TOURNAMENT_FORMATS.KNOCKOUT) {
      // Calculate knockout rounds based on power of 2
      const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
        credentials: 'include'
      });
      const tournament: Tournament = await tournamentResponse.json();
      const playerCount = tournament.players.length;
      if (playerCount <= 2) totalRounds = 1;
      else totalRounds = Math.ceil(Math.log2(playerCount));
    } else if (format === TOURNAMENT_FORMATS.ROUND_ROBIN) {
      // For round-robin, calculate expected rounds based on player count and doubleRoundRobin setting
      const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
        credentials: 'include'
      });
      const tournament: Tournament = await tournamentResponse.json();
      const playerCount = tournament.players.length;
      if (playerCount < 2) {
        scheduleContent.innerHTML = `<p>${t('schedule.noRounds', 'No rounds scheduled yet. Generate pairings to create rounds.')}</p>`;
        return;
      }
      // Round-robin: (n-1) rounds for even players, n rounds for odd players
      // Double if doubleRoundRobin is enabled
      const baseRounds = playerCount % 2 === 0 ? playerCount - 1 : playerCount;
      totalRounds = tournament.doubleRoundRobin ? baseRounds * 2 : baseRounds;
      if (existingRounds.length === 0) {
        scheduleContent.innerHTML = `<p>${t('schedule.noRounds', 'No rounds scheduled yet. Generate pairings to create rounds.')}</p>`;
        return;
      }
    }

    if (totalRounds === 0) {
      scheduleContent.innerHTML = `<p>${t('schedule.noRounds', 'No rounds scheduled yet.')}</p>`;
      return;
    }

    // Render schedule form
    let html = '<div class="schedule-form">';
    html += '<table class="schedule-table">';
    html += '<thead><tr>';
    html += `<th>${t('schedule.round', 'Round')}</th>`;
    html += `<th>${t('schedule.date', 'Date')}</th>`;
    html += `<th>${t('schedule.time', 'Time')}</th>`;
    html += '</tr></thead><tbody>';

    for (let i = 1; i <= totalRounds; i++) {
      const round = roundsMap[i];
      html += '<tr>';
      html += `<td>${t('round.round', 'Round')} ${i}</td>`;
      html += `<td><input type="date" class="schedule-date-input" data-tournament="${tournamentId}" data-round="${i}" value="${round?.roundDate || ''}" /></td>`;
      html += `<td><input type="time" class="schedule-time-input" data-tournament="${tournamentId}" data-round="${i}" value="${round?.startTime || ''}" /></td>`;
      html += '</tr>';
    }

    html += '</tbody></table>';
    html += `<button class="btn btn-primary" onclick="saveAllScheduleChanges('${tournamentId}')" style="margin-top: 20px;">${t('schedule.saveAll', 'Save All Changes')}</button>`;
    html += '</div>';

    scheduleContent.innerHTML = html;
  } catch (error) {
    console.error('Error loading schedule:', error);
    scheduleContent.innerHTML = `<p>${t('common.error', 'Error')}: Failed to load schedule</p>`;
  }
}

/**
 * Save all schedule changes
 * @param tournamentId - The tournament ID
 */
export async function saveAllScheduleChanges(tournamentId: string): Promise<void> {
  try {
    const dateInputs = document.querySelectorAll<HTMLInputElement>(`.schedule-date-input[data-tournament="${tournamentId}"]`);
    const timeInputs = document.querySelectorAll<HTMLInputElement>(`.schedule-time-input[data-tournament="${tournamentId}"]`);

    const updates: ScheduleUpdate[] = [];
    dateInputs.forEach((dateInput, index) => {
      const roundNumber = dateInput.dataset['round'];
      const timeInput = timeInputs[index];

      if (roundNumber && timeInput) {
        updates.push({
          roundNumber: parseInt(roundNumber),
          roundDate: dateInput.value || null,
          startTime: timeInput.value || null
        });
      }
    });

    // Send updates to backend
    for (const update of updates) {
      const response = await fetch(`${API_BASE_URL}/${tournamentId}/rounds/${update.roundNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          roundDate: update.roundDate,
          startTime: update.startTime
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update round ${update.roundNumber}`);
      }
    }

    // Close the modal
    closeScheduleModal();

    // Reload single tournament to update any displayed dates
    if (window.loadSingleTournament) {
      window.loadSingleTournament(tournamentId);
    }
  } catch (error) {
    console.error('Error saving schedule:', error);
    alert(t('common.error', 'Error') + ': ' + (error as Error).message);
  }
}

// Export to window for onclick handlers

window.showRoundInfoModal = showRoundInfoModal;
window.closeRoundInfoModal = closeRoundInfoModal;
window.handleRoundInfoSubmit = handleRoundInfoSubmit;
window.openScheduleModal = openScheduleModal;
window.closeScheduleModal = closeScheduleModal;
window.saveAllScheduleChanges = saveAllScheduleChanges;
